import React from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Plus, Check, Flame, Zap, Moon, Shield, Dumbbell, HeartPulse } from "lucide-react";
import { motion } from "framer-motion";
import { fmt, proxyImg } from "../lib/format";
import { useCart } from "../contexts/CartContext";

// Pick the right badge based on the product name keywords
function pickBadge(product) {
  const name = (product.name || "").toLowerCase();
  if (product.featured) return { label: "Mais Vendido", Icon: Flame, color: "bg-brand-accent text-white" };
  if (/cafe[ií]na|guaran|energ[ií]a|maca/.test(name)) return { label: "Energia", Icon: Zap, color: "bg-amber-100 text-amber-800" };
  if (/melaton|sono|valeriana|passifl/.test(name)) return { label: "Sono", Icon: Moon, color: "bg-indigo-100 text-indigo-800" };
  if (/vitamina|zinco|sel[eê]nio|imun/.test(name)) return { label: "Imunidade", Icon: Shield, color: "bg-emerald-100 text-emerald-800" };
  if (/creatin|whey|coq10|proteína|fitness|musc/.test(name)) return { label: "Fitness", Icon: Dumbbell, color: "bg-rose-100 text-rose-800" };
  if (/col[aá]geno|cabelo|pele|unha/.test(name)) return { label: "Beleza", Icon: HeartPulse, color: "bg-pink-100 text-pink-800" };
  return null;
}

// Pick benefits to display (based on category/name)
function pickBenefits(product) {
  // Use product-specific beneficios if provided (from MongoDB)
  if (Array.isArray(product.beneficios) && product.beneficios.length > 0) {
    return product.beneficios;
  }
  const name = (product.name || "").toLowerCase();
  if (/col[aá]geno/.test(name)) return ["Pele & unhas", "Articulações", "Aporte proteico"];
  if (/guaran|cafe[ií]na|maca/.test(name)) return ["Mais disposição", "Foco mental", "Energia natural"];
  if (/melaton|valeriana|passifl/.test(name)) return ["Sono profundo", "Relaxa o sistema", "Bem-estar"];
  if (/vitamina|zinco|sel[eê]nio/.test(name)) return ["Reforço imune", "Antioxidante", "Vitalidade"];
  if (/creatin|whey/.test(name)) return ["Recuperação muscular", "Mais força", "Performance"];
  if (/[oó]leo|c[aá]psula|fitoter/.test(name)) return ["Matéria-prima premium", "Fórmula manipulada", "Pureza testada"];
  return ["Manipulação premium", "Qualidade Biofarmax", "Frete rápido"];
}

export default function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  const badge = pickBadge(product);
  const benefits = pickBenefits(product);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="card-product group flex flex-col"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/produto/${product.slug}`} className="block relative overflow-hidden rounded-2xl aspect-square mb-4 shine-on-hover"
        style={{
          background:
            "radial-gradient(120% 90% at 50% 110%, rgba(201,180,122,0.10) 0%, transparent 55%), linear-gradient(180deg, #F7F4EB 0%, #E5DFCA 100%)",
        }}
      >
        <img
          src={proxyImg(product.thumbnail || product.images?.[0])}
          alt={product.name}
          className="w-full h-full object-contain p-5 transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
        />
        {/* badges */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2 pointer-events-none">
          {badge ? (
            <span className={`badge-pill ${badge.color}`}>
              <badge.Icon size={11} strokeWidth={2} /> {badge.label}
            </span>
          ) : (
            <span />
          )}
          {product.on_sale && (
            <span className="badge-pill bg-rose-600 text-white">
              <Flame size={11} strokeWidth={2} /> Promo
            </span>
          )}
        </div>
        {/* decorative ring */}
        <div className="absolute bottom-3 right-3 w-12 h-12 rounded-full border border-brand-primary/10 group-hover:border-brand-accent/40 transition-colors" />
      </Link>

      <Link to={`/produto/${product.slug}`} className="flex-1">
        <h3 className="font-heading text-base sm:text-lg leading-tight mb-2 group-hover:text-brand-accent transition-colors line-clamp-2 min-h-[2.5rem] sm:min-h-[3rem]">
          {product.name}
        </h3>
      </Link>

      {/* Benefits checklist - hidden on tiny screens to keep grid-2-cols clean */}
      <ul className="hidden sm:block space-y-1.5 mb-4 text-xs text-ink-secondary">
        {benefits.slice(0, 3).map((b, i) => (
          <li key={i} className="flex items-center gap-2">
            <Check size={13} className="text-brand-accent flex-shrink-0" strokeWidth={2.4} />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="flex items-end justify-between pt-3 border-t border-black/5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-ink-secondary font-tech">a partir de</div>
          <div className="font-heading text-2xl text-brand-primary">{fmt(product.price)}</div>
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            add(product);
          }}
          className="bg-brand-primary text-white p-3 rounded-full hover:bg-brand-accent hover:rotate-90 transition-all duration-300 shadow-[0_10px_24px_-10px_rgba(15,15,15,0.45)]"
          aria-label="Adicionar ao carrinho"
          data-testid={`add-cart-${product.slug}`}
        >
          <Plus size={18} strokeWidth={2} />
        </button>
      </div>
    </motion.div>
  );
}
